import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type { AutomationSchedule, AutomationTarget } from "@/lib/types";

import {
  fetchAutomations,
  fetchAutomation,
  fetchAutomationRuns,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  triggerAutomation,
  cancelAutomationRun,
  pauseAutomation,
  resumeAutomation,
} from "@/lib/api";

export function useAutomations() {
  return useQuery({
    queryKey: ["automations"],
    queryFn: fetchAutomations,
  });
}

export function useAutomation(id: string | null) {
  return useQuery({
    queryKey: ["automation", id],
    queryFn: () => fetchAutomation(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.runs.some((r) => r.status === "running");
      return hasRunning ? 3000 : false;
    },
  });
}

export function useAutomationRuns(id: string | null) {
  return useQuery({
    queryKey: ["automation-runs", id],
    queryFn: () => fetchAutomationRuns(id!),
    enabled: !!id,
  });
}

export function useCreateAutomation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      prompt: string;
      schedule: AutomationSchedule;
      target?: AutomationTarget;
      modelId?: string | null;
    }) => createAutomation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
    },
  });
}

export function useUpdateAutomation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      prompt?: string;
      schedule?: AutomationSchedule;
      target?: AutomationTarget;
      modelId?: string | null;
      status?: "enabled" | "disabled";
    }) => updateAutomation(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
      queryClient.invalidateQueries({ queryKey: ["automation", variables.id] });
    },
  });
}

export function useDeleteAutomation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAutomation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
    },
  });
}

export function useTriggerAutomation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => triggerAutomation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
      queryClient.invalidateQueries({ queryKey: ["automation", id] });
    },
  });
}

export function useCancelAutomationRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ automationId, runId }: { automationId: string; runId: string }) =>
      cancelAutomationRun(automationId, runId),
    onSuccess: (_data, { automationId }) => {
      queryClient.invalidateQueries({ queryKey: ["automation", automationId] });
      queryClient.invalidateQueries({ queryKey: ["automation-runs", automationId] });
    },
  });
}

export function usePauseAutomation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pauseAutomation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
      queryClient.invalidateQueries({ queryKey: ["automation", id] });
    },
  });
}

export function useResumeAutomation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resumeAutomation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
      queryClient.invalidateQueries({ queryKey: ["automation", id] });
    },
  });
}
